import { Button, FormControlLabel } from '@material-ui/core';
import Checkbox from '@material-ui/core/Checkbox';
import CssBaseline from '@material-ui/core/CssBaseline';
import { MuiThemeProvider, createMuiTheme } from '@material-ui/core/styles';
import * as React from 'react';
import * as ReactDOM from 'react-dom';
import { BetaBadge } from './BetaBadge';

export const PluginIntro: React.FC = () => {
  return (
    <article
      style={{
        display: 'flex',
        maxWidth: '308px',
        flexDirection: 'column',
        fontFamily: 'Inter, sans-serif',
        justifyContent: 'start',
        outline: 'none',
      }}
      role="region"
      aria-labelledby="plugin-title"
    >
      <img
        loading="lazy"
        src="https://cdn.builder.io/api/v1/image/assets/f5348105e75441b59830f1e489577801/1f601258d25809d0bea9bd1f76688db5117fa97bd8c49bc01b24e2fe37939c4b?apiKey=f5348105e75441b59830f1e489577801&"
        alt="Layout copy feature illustration"
        style={{
          aspectRatio: '1.36',
          objectFit: 'contain',
          objectPosition: 'center',
          width: '140px',
          borderRadius: '0px',
          alignSelf: 'center',
          maxWidth: '100%',
          cursor: 'default',
        }}
      />
      <section
        style={{
          display: 'flex',
          margin: '16px 0 0',
          width: '100%',
          flexDirection: 'column',
          justifyContent: 'start',
        }}
      >
        <h2
          id="plugin-title"
          style={{
            color: 'var(--text-primary, #333)',
            fontSize: '18px',
            fontWeight: 600,
            lineHeight: 1.2,
            margin: 0,
          }}
        >
          Copy Layout
        </h2>
        <p
          style={{
            alignSelf: 'start',
            color: 'var(--text-secondary, #5C5C5C)',
            textAlign: 'center',
            fontSize: '14px',
            fontWeight: 400,
            lineHeight: '20px',
            margin: '8px 0 0',
          }}
        >
          Save time by copying layouts from the web directly into Builder or Figma
        </p>
      </section>
    </article>
  );
};

const primaryColor = 'rgb(26, 115, 235, 1)';
const primaryDarkColor = 'rgba(0, 82, 187, 1)';
const REVIEW_URL =
  'https://chromewebstore.google.com/detail/builderio/cfldfgibklhmjhnkfighkbafbkbfcmij/reviews';

const theme = createMuiTheme({
  palette: {
    primary: { main: primaryColor, dark: primaryDarkColor },
  },
  overrides: {
    MuiButton: {
      contained: {
        boxShadow: 'none !important',
        '&:active': {
          boxShadow: 'none !important',
        },
      },
      root: {
        borderRadius: '6px',
        textTransform: 'capitalize' as const,
        boxShadow: 'none',
        '&:active': {
          boxShadow: 'none',
        },
        fontSize: '1rem',
        lineHeight: '1.75rem',
      },
    },
  },
});

declare const chrome: any;

type RootState = {
  outlineBuilderComponents: boolean;
  fusionMode: boolean;
  promptMode: boolean;
  userPrompt: string;
  clickCount: number;
  activeTab: 'copy' | 'agent';
  showReviewPrompt: boolean;
  hideAgentIntro: boolean;
};
class RootComponent extends React.Component<{}, RootState> {
  constructor(props) {
    super(props);
    this.state = {
      outlineBuilderComponents: false,
      fusionMode: false,
      promptMode: false,
      userPrompt: '',
      clickCount: 0,
      activeTab: 'copy',
      showReviewPrompt: false,
      hideAgentIntro: false,
    };
  }

  builderOutlineStylesClass = 'builder-outline-elements-style';
  builderEditClassName = 'builder-edit-extension';
  builderLogoSrc =
    'https://cdn.builder.io/api/v1/image/assets%2FYJIGb4i01jvw0SRdL5Bt%2F1d1897131896467986b4bc5d9e8d3c6e';

  outlineElements = () => {
    const shouldOutline = this.state.outlineBuilderComponents;

    chrome.tabs.query({ active: true, currentWindow: true }, function (tabs) {
      chrome.tabs.sendMessage(tabs[0].id, {
        type: 'outlineElements',
        payload: {
          shouldOutline: shouldOutline,
          builderOutlineStylesClass: 'builder-outline-elements-style',
          builderEditClassName: 'builder-edit-extension',
        },
      });
    });
  };

  handleOutlineBuilderElementsClick = (event: React.ChangeEvent<HTMLInputElement>) => {
    const checked = Boolean(event.target.checked);
    chrome.storage.sync.set({ outlineBuilderComponents: checked });
    this.setState({ outlineBuilderComponents: checked }, () => {
      this.outlineElements();
    });
  };

  copyLayout = (event: React.MouseEvent<HTMLButtonElement>) => {
    chrome.runtime.sendMessage({ type: 'copyLayout', payload: {} }, () => {
      // Add an additional split second delay to make sure the window has closed to
      // we get an immediate hover event which manes the overlays show right away
      setTimeout(() => {
        window.close();
      }, 100);
    });
  };

  // Fusion Extension - Element selection functionality
  startFusionSelection = () => {
    chrome.runtime.sendMessage({ type: 'START_SELECTION' }, response => {
      if (response && response.success) {
        // Close popup immediately so user can interact with the page
        window.close();
      } else {
        console.error('Selection failed:', response);
      }
    });
  };

  captureAndCreateAgent = async () => {
    if (!this.state.userPrompt.trim()) {
      alert('Please enter a prompt describing what you want to change.');
      return;
    }

    try {
      console.log('🚀 Starting comprehensive data capture...');

      // Step 1: Get selected element data
      chrome.runtime.sendMessage({ type: 'GET_SELECTED_ELEMENT' }, elementResponse => {
        if (elementResponse && elementResponse.success && elementResponse.element) {
          // Step 2: Capture screenshot
          chrome.runtime.sendMessage({ type: 'CAPTURE_SCREENSHOT' }, screenshotResponse => {
            if (screenshotResponse && screenshotResponse.success) {
              // Step 3: Capture DOM snapshot
              chrome.runtime.sendMessage({ type: 'CAPTURE_DOM_SNAPSHOT' }, domResponse => {
                if (domResponse && domResponse.success) {
                  // Generate unique ID and prepare comprehensive project data
                  const htmlId =
                    'fusion_' + Date.now().toString(36) + Math.random().toString(36).substr(2);
                  const projectData = {
                    htmlId,
                    url: domResponse.data.url || window.location.href,
                    timestamp: new Date().toISOString(),
                    userPrompt: this.state.userPrompt.trim(),
                    element: elementResponse.element,
                    screenshot: screenshotResponse.data,
                    domSnapshot: domResponse.data.domSnapshot,
                    pageTitle: domResponse.data.pageTitle,
                    message: `AI Agent Task: "${this.state.userPrompt.trim()}" for element: ${
                      elementResponse.element.tagName
                    }${elementResponse.element.id ? '#' + elementResponse.element.id : ''}${
                      elementResponse.element.className
                        ? '.' + elementResponse.element.className.split(' ').join('.')
                        : ''
                    }`,
                  };

                  // Store in chrome storage
                  chrome.storage.local.set(
                    {
                      [`fusion_project_${htmlId}`]: projectData,
                    },
                    () => {
                      console.log('🎯 AI Agent project data stored with ID:', htmlId);

                      // Clear selection and reset UI
                      chrome.runtime.sendMessage({ type: 'CLEAR_SELECTION' });
                      this.setState({
                        fusionMode: false,
                        promptMode: false,
                        userPrompt: '',
                      });

                      // Open the AI agent interface
                      const agentUrl = `https://builder.io/app/projects/extension-agent/${htmlId}`;
                      chrome.tabs.create({ url: agentUrl });

                      // Close popup only after successful agent creation
                      setTimeout(() => {
                        window.close();
                      }, 1000);
                    }
                  );
                } else {
                  console.error('❌ Failed to capture DOM snapshot:', domResponse);
                  alert('Failed to capture page content. Please try again.');
                }
              });
            } else {
              console.error('❌ Failed to capture screenshot:', screenshotResponse);
              alert('Failed to capture screenshot. Please try again.');
            }
          });
        } else {
          console.error('❌ No element selected:', elementResponse);
          alert('No element selected. Please select an element first.');
        }
      });
    } catch (error) {
      console.error('❌ Failed to capture and store:', error);
      alert('An error occurred. Please try again.');
    }
  };

  componentDidMount() {
    chrome.storage.sync.get(['outlineBuilderComponents'], data => {
      this.setState({ outlineBuilderComponents: Boolean(data.outlineBuilderComponents) }, () => {
        this.outlineElements();
      });
    });

    // Load AI Agent visibility setting and last selected tab from local storage
    chrome.storage.local.get(
      [
        'activeTab',
        'popupOpenCount',
        'reviewPromptDismissed',
        'firstPopupOpenAt',
        'hideAgentIntro',
      ],
      data => {
        const now = Date.now();
        const popupOpenCount = Number(data.popupOpenCount || 0) + 1;
        let firstPopupOpenAt = Number(data.firstPopupOpenAt);
        const shouldPersistFirstOpen = !data.firstPopupOpenAt || Number.isNaN(firstPopupOpenAt);

        if (shouldPersistFirstOpen) {
          firstPopupOpenAt = now;
        }

        const storageUpdate: Record<string, number> = { popupOpenCount };
        if (shouldPersistFirstOpen) {
          storageUpdate.firstPopupOpenAt = firstPopupOpenAt;
        }

        chrome.storage.local.set(storageUpdate);

        const oneWeekMs = 7 * 24 * 60 * 60 * 1000;
        const hasWeekElapsed = now - firstPopupOpenAt >= oneWeekMs;
        const shouldShowReview =
          !data.reviewPromptDismissed && popupOpenCount >= 4 && hasWeekElapsed;

        this.setState({
          activeTab: data.activeTab || 'copy',
          showReviewPrompt: shouldShowReview, // shouldShowReview,
          hideAgentIntro: Boolean(data.hideAgentIntro),
        });
      }
    );

    // Clear any badge notifications when popup opens
    try {
      chrome.action.setBadgeText({ text: '' });
    } catch (error) {
      console.log('Could not clear badge:', error);
    }

    // Check if there's a selected element for fusion functionality
    this.checkFusionState();
  }

  checkFusionState = () => {
    chrome.runtime.sendMessage({ type: 'GET_SELECTED_ELEMENT' }, response => {
      if (response && response.success && response.element) {
        this.setState({ fusionMode: true, promptMode: true });
      }
    });
  };

  handleReviewClick = () => {
    chrome.tabs.create({ url: REVIEW_URL });
    chrome.storage.local.set({ reviewPromptDismissed: true });
    this.setState({ showReviewPrompt: false });
  };

  dismissReviewPrompt = () => {
    chrome.storage.local.set({ reviewPromptDismissed: true });
    this.setState({ showReviewPrompt: false });
  };

  handleHideAgentIntroChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const hideAgentIntro = event.target.checked;
    chrome.storage.local.set({ hideAgentIntro });
    this.setState({ hideAgentIntro });
  };

  renderHeader = () => {
    const headerStyle: React.CSSProperties = {
      display: 'flex',
      alignItems: 'center',
      padding: '16px 20px',
      margin: '0 -30px',
      borderBottom: '1px solid #e0e0e0',
    };

    return (
      <div style={headerStyle}>
        <img
          src="icons/builder-logo.svg"
          alt="Builder.io"
          style={{ height: '20px', width: 'auto' }}
        />
      </div>
    );
  };

  renderTabUI = () => {
    const { activeTab } = this.state;
    const tabStyle: React.CSSProperties = {
      display: 'flex',
      gap: '20px',
      alignItems: 'center',
      margin: '0 -30px 20px',
      padding: '0 20px',
    };

    const tabItemStyle = (isActive: boolean): React.CSSProperties => ({
      display: 'flex',
      flexDirection: 'column',
      gap: '8px',
      paddingTop: '15px',
      alignItems: 'flex-start',
      justifyContent: 'center',
      cursor: 'pointer',
    });

    const tabTextStyle = (isActive: boolean): React.CSSProperties => ({
      fontSize: '14px',
      fontWeight: isActive ? 600 : 500,
      lineHeight: '20px',
      color: isActive ? '#333' : '#909090',
      textAlign: 'center' as const,
    });

    const tabUnderlineStyle = (isActive: boolean): React.CSSProperties => ({
      width: '100%',
      height: '2px',
      backgroundColor: isActive ? primaryColor : 'transparent',
    });

    return (
      <>
        {this.renderHeader()}
        <div style={tabStyle}>
          <div
            style={tabItemStyle(activeTab === 'copy')}
            onClick={() => {
              this.setState({ activeTab: 'copy' });
              chrome.storage.local.set({ activeTab: 'copy' });
            }}
          >
            <span style={tabTextStyle(activeTab === 'copy')}>Copy Layout</span>
            <div style={tabUnderlineStyle(activeTab === 'copy')} />
          </div>
          <div
            style={tabItemStyle(activeTab === 'agent')}
            onClick={() => {
              this.setState({ activeTab: 'agent' });
              chrome.storage.local.set({ activeTab: 'agent' });
            }}
          >
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <span style={tabTextStyle(activeTab === 'agent')}>Builder Agent</span>
              <BetaBadge
                style={{
                  padding: '2px 4px',
                  fontSize: '11px',
                  fontWeight: 600,
                  lineHeight: '16px',
                  letterSpacing: '0.5px',
                }}
              />
            </div>
            <div style={tabUnderlineStyle(activeTab === 'agent')} />
          </div>
        </div>
      </>
    );
  };

  render() {
    return (
      <MuiThemeProvider theme={theme}>
        <div style={{ padding: '0 30px 0px 30px', width: 380, backgroundColor: 'white' }}>
          <CssBaseline />
          <div style={{ textAlign: 'center' }}>
            {/* Show tabs if AI Agent is enabled, otherwise show normal UI */}
            {this.state.promptMode && this.renderHeader()}
            {!this.state.promptMode && this.renderTabUI()}

            {/* Copy Layouts UI - show when Copy tab is active or AI Agent is disabled */}
            {!this.state.promptMode && this.state.activeTab === 'copy' && (
              <>
                <PluginIntro />
                {this.state.showReviewPrompt && (
                  <div
                    style={{
                      backgroundColor: '#F8FAFF',
                      border: '1px solid #E2E9F8',
                      borderRadius: '8px',
                      padding: '12px',
                      margin: '18px 0 0 0',
                      textAlign: 'left',
                    }}
                  >
                    <p
                      style={{
                        fontSize: '13px',
                        fontWeight: 600,
                        color: '#1f3d7a',
                        margin: '0 0 4px 0',
                      }}
                    >
                      Enjoying Builder?
                    </p>
                    <p
                      style={{
                        fontSize: '12px',
                        color: '#4f5b76',
                        margin: 0,
                        lineHeight: '18px',
                      }}
                    >
                      We'd appreciate a quick Chrome Web Store review when you have a minute.
                    </p>
                    <div
                      style={{
                        display: 'flex',
                        gap: '8px',
                        flexWrap: 'wrap',
                        marginTop: '12px',
                      }}
                    >
                      <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        onClick={this.handleReviewClick}
                      >
                        Leave a Review
                      </Button>
                      <Button
                        variant="text"
                        size="small"
                        onClick={this.dismissReviewPrompt}
                        style={{ color: '#5f6368' }}
                      >
                        Not now
                      </Button>
                    </div>
                  </div>
                )}
                <Button
                  style={{
                    marginTop: 30,
                    marginBottom: 10,
                  }}
                  fullWidth
                  variant="contained"
                  color="primary"
                  onClick={this.copyLayout}
                >
                  Copy Layout
                </Button>

                <FormControlLabel
                  style={{
                    userSelect: 'none',
                    marginBottom: 15,
                    transform: 'scale(0.9)',
                  }}
                  control={
                    <Checkbox
                      checked={this.state.outlineBuilderComponents}
                      onChange={this.handleOutlineBuilderElementsClick}
                      disableRipple
                      color="primary"
                    />
                  }
                  label="Outline Builder components"
                />
              </>
            )}

            {/* AI Code Agent UI - show when Agent tab is active and AI Agent is enabled */}
            {((!this.state.promptMode && this.state.activeTab === 'agent') ||
              this.state.promptMode) && (
              <div
                style={{
                  marginTop: this.state.promptMode ? 0 : 0,
                  paddingTop: this.state.promptMode ? 0 : 0,
                  borderTop: 'none',
                }}
              >
                {!this.state.fusionMode ? (
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: '16px',
                      padding: '20px 0',
                    }}
                  >
                    {/* Header Section */}
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: '8px',
                        width: '100%',
                      }}
                    >
                      <h2
                        style={{
                          fontSize: '20px',
                          fontWeight: 600,
                          lineHeight: '24px',
                          color: '#333',
                          margin: 0,
                          textAlign: 'center',
                        }}
                      >
                        Ship UI Changes Faster
                      </h2>
                      <p
                        style={{
                          fontSize: '14px',
                          fontWeight: 400,
                          lineHeight: '20px',
                          color: '#5c5c5c',
                          margin: 0,
                          textAlign: 'center',
                        }}
                      >
                        Select any element, describe what you want, and Builder will get a
                        ready-to-review PR
                      </p>
                    </div>

                    {/* How it works Card */}
                    {!this.state.hideAgentIntro && (
                      <div
                        style={{
                          backgroundColor: '#f5f5f5',
                          border: '1px solid #d5d5d5',
                          borderRadius: '8px',
                          padding: '16px',
                          width: '100%',
                          boxSizing: 'border-box',
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '16px',
                        }}
                      >
                        <p
                          style={{
                            fontSize: '14px',
                            fontWeight: 500,
                            lineHeight: '20px',
                            color: '#333',
                            margin: 0,
                          }}
                        >
                          How it works:
                        </p>
                        <ol
                          style={{
                            fontSize: '14px',
                            fontWeight: 400,
                            lineHeight: '20px',
                            color: '#5c5c5c',
                            margin: 0,
                            paddingLeft: '21px',
                            textAlign: 'left',
                          }}
                        >
                          <li>Select an element on the page</li>
                          <li>Prompt a change you want to make</li>
                          <li>Builder will start working on your code</li>
                          <li>PR will be created for you to review</li>
                        </ol>
                        <FormControlLabel
                          style={{
                            margin: 0,
                            userSelect: 'none',
                          }}
                          control={
                            <Checkbox
                              checked={this.state.hideAgentIntro}
                              onChange={this.handleHideAgentIntroChange}
                              disableRipple
                              size="small"
                              style={{
                                padding: '0 8px 0 0',
                                color: '#d5d5d5',
                              }}
                            />
                          }
                          label={
                            <span
                              style={{
                                fontSize: '14px',
                                fontWeight: 400,
                                lineHeight: '20px',
                                color: '#5c5c5c',
                              }}
                            >
                              Don't show this again
                            </span>
                          }
                        />
                      </div>
                    )}

                    {/* Action Buttons */}
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: '12px',
                        width: '100%',
                      }}
                    >
                      <Button
                        style={{
                          padding: '8px 12px',
                          fontSize: '16px',
                          fontWeight: 500,
                          lineHeight: '24px',
                        }}
                        fullWidth
                        variant="contained"
                        color="primary"
                        onClick={this.startFusionSelection}
                      >
                        Start
                      </Button>
                      <a
                        href="https://www.builder.io/c/docs/fusion"
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '4px',
                          fontSize: '14px',
                          fontWeight: 500,
                          lineHeight: '20px',
                          color: primaryColor,
                          textDecoration: 'none',
                          padding: '6px 8px',
                        }}
                      >
                        <svg
                          width="18"
                          height="18"
                          viewBox="0 0 18 18"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M13.5 9.75V14.25C13.5 14.6478 13.342 15.0294 13.0607 15.3107C12.7794 15.592 12.3978 15.75 12 15.75H3.75C3.35218 15.75 2.97064 15.592 2.68934 15.3107C2.40804 15.0294 2.25 14.6478 2.25 14.25V6C2.25 5.60218 2.40804 5.22064 2.68934 4.93934C2.97064 4.65804 3.35218 4.5 3.75 4.5H8.25"
                            stroke={primaryColor}
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M11.25 2.25H15.75V6.75"
                            stroke={primaryColor}
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M7.5 10.5L15.75 2.25"
                            stroke={primaryColor}
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                        Learn More
                      </a>
                    </div>
                  </div>
                ) : !this.state.promptMode ? (
                  <>
                    <p
                      style={{
                        fontSize: '12px',
                        color: '#10b981',
                        margin: '0 0 10px 0',
                        fontWeight: 500,
                        lineHeight: '18px',
                      }}
                    >
                      ✅ Element selected successfully!
                      <br />
                      Ready to create your AI code agent?
                    </p>
                    <Button
                      style={{
                        marginBottom: 8,
                        padding: '8px 16px',
                        fontSize: '14px',
                      }}
                      fullWidth
                      variant="contained"
                      color="primary"
                      onClick={() => this.setState({ promptMode: true })}
                    >
                      Create AI Agent
                    </Button>
                    <Button
                      style={{
                        padding: '6px 16px',
                        fontSize: '13px',
                      }}
                      fullWidth
                      variant="outlined"
                      onClick={() => {
                        chrome.runtime.sendMessage({ type: 'CLEAR_SELECTION' });
                        this.setState({ fusionMode: false });
                      }}
                    >
                      Cancel
                    </Button>
                  </>
                ) : (
                  <>
                    <p
                      style={{
                        fontSize: '12px',
                        color: '#333',
                        margin: '0 0 10px 0',
                        fontWeight: 500,
                      }}
                    >
                      What code changes do you want to make?
                    </p>
                    <textarea
                      value={this.state.userPrompt}
                      onChange={e => this.setState({ userPrompt: e.target.value })}
                      onKeyDown={e => {
                        if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                          e.preventDefault();
                          // Use currentTarget.value to avoid React state race condition
                          const currentValue = (e.currentTarget as HTMLTextAreaElement).value;
                          if (currentValue.trim()) {
                            this.captureAndCreateAgent();
                          }
                        }
                      }}
                      placeholder="e.g., Change this button to be red and add a hover effect..."
                      style={{
                        width: '100%',
                        minHeight: '80px',
                        padding: '8px',
                        fontSize: '13px',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        resize: 'vertical',
                        fontFamily: 'inherit',
                        marginBottom: '10px',
                        boxSizing: 'border-box',
                      }}
                      maxLength={500}
                    />
                    <div style={{ fontSize: '11px', color: '#666', marginBottom: '10px' }}>
                      {this.state.userPrompt.length}/500 characters
                    </div>
                    <Button
                      style={{
                        marginBottom: 8,
                        padding: '8px 16px',
                        fontSize: '14px',
                      }}
                      fullWidth
                      variant="contained"
                      color="primary"
                      onClick={this.captureAndCreateAgent}
                      disabled={!this.state.userPrompt.trim()}
                    >
                      Create AI Agent
                    </Button>
                    <Button
                      style={{
                        padding: '6px 16px',
                        fontSize: '13px',
                      }}
                      fullWidth
                      variant="outlined"
                      onClick={() => {
                        this.setState({ promptMode: false, userPrompt: '' });
                      }}
                    >
                      Back
                    </Button>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </MuiThemeProvider>
    );
  }
}

ReactDOM.render(<RootComponent />, document.getElementById('root'));
